/**
 * 从视频文件获取封面和时长
 * 优先：MP4/MOV 等从文件头解析 mvhd 时长 + ilst/covr 内嵌封面（不整段解码）
 * 回退：<video> + Canvas 抽帧（兼容无内嵌封面、WebM 等）
 */

import { isProbablyMp4FamilyFile, tryReadMp4EmbeddedMetadata } from "./mp4FastMetadata";

export interface VideoMetadata {
  duration: number; // 秒，小数点后三位
  coverBlob: Blob; // 封面图片的Blob对象
  coverFileName: string; // 封面文件名（基于视频文件名生成）
}

/**
 * 获取视频元数据（时长和封面）
 * @param file 视频文件
 * @returns Promise<VideoMetadata> 视频时长和封面
 */
function getVideoMetadataViaVideoElement(file: File): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    // 清理之前的视频元素
    let currentVideoElement: HTMLVideoElement | null = null;
    let videoBlobUrl: string | null = null;

    const cleanup = () => {
      if (wallWatchId) {
        clearTimeout(wallWatchId);
        wallWatchId = null;
      }
      if (visibilityListener) {
        try {
          document.removeEventListener('visibilitychange', visibilityListener);
        } catch {
          /* ignore */
        }
        visibilityListener = null;
      }
      if (currentVideoElement) {
        currentVideoElement.src = '';
        currentVideoElement.load();
        currentVideoElement = null;
      }
      if (videoBlobUrl) {
        URL.revokeObjectURL(videoBlobUrl);
        videoBlobUrl = null;
      }
    };

    // 创建视频元素（不插入DOM，仅用于处理元数据）
    const video = document.createElement('video');
    currentVideoElement = video;
    
    // 关键：需要加载足够的数据来获取第一帧
    video.preload = 'auto';
    video.autoplay = false;
    video.muted = true;
    video.crossOrigin = 'anonymous';

    // 创建Canvas用于生成封面
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      cleanup();
      reject(new Error('无法创建Canvas上下文'));
      return;
    }

    let coverGenerated = false;
    let timeoutId: NodeJS.Timeout | null = null;
    let wallWatchId: NodeJS.Timeout | null = null;
    let visibilityListener: (() => void) | null = null;
    let retryCount = 0;
    const MAX_RETRY = 5;

    // 清理函数
    const clearTimeoutHandler = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // 生成封面的函数
    const generateCover = () => {
      if (coverGenerated) return;
      
      try {
        // 确保视频尺寸已加载
        if (video.videoWidth === 0 || video.videoHeight === 0) {
          // 如果尺寸为0，重试几次
          if (retryCount < MAX_RETRY) {
            retryCount++;
            setTimeout(() => {
              if (!coverGenerated && video.videoWidth > 0 && video.videoHeight > 0) {
                clearTimeoutHandler();
                coverGenerated = true;
                try {
                  const coverBlob = generateVideoCover(video, canvas, ctx);
                  const coverFileName = getCoverFileName(file.name);
                  // 保留3位小数
                  const duration = parseFloat(video.duration.toFixed(3));
                  resolve({ duration, coverBlob, coverFileName });
                  cleanup();
                } catch (err: any) {
                  cleanup();
                  reject(new Error('生成视频封面失败：' + err.message));
                }
              } else if (!coverGenerated && retryCount >= MAX_RETRY) {
                clearTimeoutHandler();
                cleanup();
                reject(new Error('无法获取视频尺寸'));
              }
            }, 100 * retryCount); // 递增延迟
          } else {
            clearTimeoutHandler();
            cleanup();
            reject(new Error('无法获取视频尺寸'));
          }
          return;
        }
        
        // 确保视频帧已准备好（readyState >= HAVE_CURRENT_DATA）
        if (video.readyState < video.HAVE_CURRENT_DATA) {
          // 如果还没准备好，等待一下
          if (retryCount < MAX_RETRY) {
            retryCount++;
            setTimeout(generateCover, 100);
          } else {
            clearTimeoutHandler();
            cleanup();
            reject(new Error('视频帧数据未准备好'));
          }
          return;
        }
        
        clearTimeoutHandler();
        coverGenerated = true;
        // 生成第一帧封面
        const coverBlob = generateVideoCover(video, canvas, ctx);
        const coverFileName = getCoverFileName(file.name);
        // 保留3位小数
        const duration = parseFloat(video.duration.toFixed(3));
        resolve({ duration, coverBlob, coverFileName });
        cleanup();
      } catch (err: any) {
        clearTimeoutHandler();
        if (!coverGenerated) {
          coverGenerated = true;
          cleanup();
          reject(new Error('生成视频封面失败：' + err.message));
        }
      }
    };

    // 监听视频元数据加载完成事件（获取时长）
    video.addEventListener('loadedmetadata', () => {
      try {
        // 获取视频时长（秒数，可能是小数）
        const duration = video.duration;
        if (isNaN(duration) || duration === 0) {
          cleanup();
          reject(new Error('无法获取视频时长'));
          return;
        }

        // 设置视频当前时间为0.01秒（接近第一帧，确保触发seeked事件）
        video.currentTime = 0.01;
      } catch (err: any) {
        cleanup();
        reject(new Error('处理视频元数据失败：' + err.message));
      }
    }, { once: true });

    // 监听视频跳转完成事件（确保已跳转到指定帧，可以绘制）
    video.addEventListener('seeked', () => {
      // 确保视频帧已准备好
      if (video.readyState >= video.HAVE_CURRENT_DATA) {
        generateCover();
      }
    }, { once: true });

    // 监听视频数据加载完成事件（第一帧数据已加载）
    video.addEventListener('loadeddata', () => {
      // 如果当前时间已经是0.01，直接生成封面
      if (Math.abs(video.currentTime - 0.01) < 0.1) {
        generateCover();
      }
    }, { once: true });

    // 监听视频可以播放事件（备用方案）
    video.addEventListener('canplay', () => {
      if (!coverGenerated && video.currentTime >= 0 && video.currentTime <= 0.1) {
        generateCover();
      }
    }, { once: true });

    // 超时：按体积给足时间。后台 Tab 解码/计时器被节流，「软超时」只在页面可见时计时；切到后台则暂停软超时，回前台重新给满 coverTimeoutMs。
    const mb = Math.max(0, file.size / (1024 * 1024));
    const coverTimeoutMs = Math.min(
      120_000,
      Math.max(20_000, 20_000 + Math.floor(mb) * 3_000)
    );
    const coverStartedAt = Date.now();
    const MAX_COVER_WALL_MS = 15 * 60 * 1000;

    const clearWallWatch = () => {
      if (wallWatchId) {
        clearTimeout(wallWatchId);
        wallWatchId = null;
      }
    };

    const rejectCoverTimeout = () => {
      clearTimeoutHandler();
      clearWallWatch();
      cleanup();
      reject(new Error("生成视频封面超时，请重试"));
    };

    /** 仅在当前页面可见时计时；若触发瞬间又进了后台则改走墙钟监视 */
    const armSoftDeadline = () => {
      clearTimeoutHandler();
      timeoutId = setTimeout(() => {
        if (coverGenerated) return;
        if (Date.now() - coverStartedAt > MAX_COVER_WALL_MS) {
          rejectCoverTimeout();
          return;
        }
        if (typeof document !== "undefined" && document.hidden) {
          clearTimeoutHandler();
          scheduleWallWatch();
          return;
        }
        rejectCoverTimeout();
      }, coverTimeoutMs) as any;
    };

    /** 后台期间只检查绝对墙钟，避免因软超时在「刚切回前台」的瞬间误杀 */
    const scheduleWallWatch = () => {
      clearWallWatch();
      const tick = () => {
        if (coverGenerated) return;
        if (Date.now() - coverStartedAt > MAX_COVER_WALL_MS) {
          rejectCoverTimeout();
          return;
        }
        if (typeof document !== "undefined" && document.hidden) {
          wallWatchId = setTimeout(tick, 2000) as any;
        }
      };
      wallWatchId = setTimeout(tick, 2000) as any;
    };

    visibilityListener = () => {
      if (coverGenerated || typeof document === "undefined") return;
      if (document.hidden) {
        clearTimeoutHandler();
        scheduleWallWatch();
        return;
      }
      clearWallWatch();
      retryCount = 0;
      armSoftDeadline();
      try {
        generateCover();
      } catch {
        /* ignore */
      }
    };
    document.addEventListener("visibilitychange", visibilityListener);

    if (typeof document !== "undefined" && document.hidden) {
      scheduleWallWatch();
    } else {
      armSoftDeadline();
    }

    // 监听视频加载错误
    video.addEventListener('error', (err) => {
      cleanup();
      reject(new Error('视频加载失败：' + ((err.target as HTMLVideoElement)?.error?.message || '未知错误')));
    });

    // 生成Blob URL
    if (videoBlobUrl) {
      URL.revokeObjectURL(videoBlobUrl);
    }
    videoBlobUrl = URL.createObjectURL(file);
    video.src = videoBlobUrl;
  });
}

/**
 * 生成视频第一帧封面（Canvas API）
 * @param video 视频元素
 * @param canvas Canvas元素
 * @param ctx Canvas 2D上下文
 * @returns Blob 封面图片的Blob对象
 */
function generateVideoCover(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D
): Blob {
  // 设置Canvas尺寸与视频一致（保持比例）
  const width = video.videoWidth;
  const height = video.videoHeight;
  canvas.width = width;
  canvas.height = height;

  // 绘制视频第一帧
  ctx.drawImage(video, 0, 0, width, height);

  // 转换为Blob（使用JPEG格式，质量0.8）
  // 注意：toBlob是异步的，但这里我们需要同步返回，所以使用dataURL转Blob
  const dataURL = canvas.toDataURL('image/jpeg', 0.8);
  
  // 将dataURL转换为Blob
  const byteString = atob(dataURL.split(',')[1]);
  const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
}

/**
 * 根据视频文件名生成封面文件名
 * @param videoFileName 视频文件名（带后缀）
 * @returns 封面文件名（.png后缀）
 */
function getCoverFileName(videoFileName: string): string {
  // 获取文件名（不含后缀）
  const nameWithoutExt = videoFileName.replace(/\.[^/.]+$/, '');
  // 返回封面文件名（.png后缀）
  return `${nameWithoutExt}.png`;
}

export const getVideoMetadata = async (file: File): Promise<VideoMetadata> => {
  if (isProbablyMp4FamilyFile(file)) {
    try {
      const fast = await tryReadMp4EmbeddedMetadata(file);
      if (fast) {
        const base = file.name.replace(/\.[^/.]+$/, "");
        const ext = fast.coverMime === "image/png" ? "png" : "jpg";
        return {
          duration: fast.durationSec,
          coverBlob: fast.coverBlob,
          coverFileName: `${base}.${ext}`,
        };
      }
    } catch {
      /* 切片解析失败则回退解码 */
    }
  }
  return getVideoMetadataViaVideoElement(file);
};
